import { formatDate } from '@mobiscroll/react';
import holidays from 'holidays-norway';

// Holiday and date-related functions
export const createHolidayHandlers = (timerRef, setIsHoverDayOpen, setHolidayName, setCurrentHolidayId) => ({
    onHolidayMouseEnter: () => {
        // Clear any existing timer when hovering in
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    },
    
    onHolidayMouseLeave: () => {
        // Set a small delay before closing to allow moving to the popup
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => { 
            setIsHoverDayOpen(false); 
        }, 150); // Increased delay to 150ms
    },
    
    // Force close popup (for when moving away from both holiday and popup)
    forceClosePopup: () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setIsHoverDayOpen(false);
    },
    
    // Close popup on scroll
    closeOnScroll: () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setIsHoverDayOpen(false);
        setHolidayName(null);
        setCurrentHolidayId(null);
    },
    
    // Setup scroll event listeners to close popup
    setupScrollListeners: () => {
        const handleScroll = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            setIsHoverDayOpen(false);
            setHolidayName(null);
            setCurrentHolidayId(null);
        };

        // Listen to scroll events on various containers
        document.addEventListener('scroll', handleScroll, true);
        window.addEventListener('scroll', handleScroll, true);
        
        // Listen to scroll events on calendar-specific containers
        const calendarContainer = document.querySelector('.motion-air-calendar-wrapper');
        if (calendarContainer) {
            calendarContainer.addEventListener('scroll', handleScroll, true);
        }
        
        const timelineContainer = document.querySelector('.mbsc-timeline-grid-scroll');
        if (timelineContainer) {
            timelineContainer.addEventListener('scroll', handleScroll, true);
        }

        // Return cleanup function
        return () => {
            document.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('scroll', handleScroll, true);
            if (calendarContainer) {
                calendarContainer.removeEventListener('scroll', handleScroll, true);
            }
            if (timelineContainer) {
                timelineContainer.removeEventListener('scroll', handleScroll, true);
            }
        };
    },
    
    // Add cleanup function to reset holiday state
    cleanupHolidayState: (holidayAnchor, setHolidayName) => {
        if (holidayAnchor && holidayAnchor.current) {
            holidayAnchor.current = null;
        }
        setHolidayName(null);
    }
});

export const createMyDayContent = (norwegianHolidays, setIsHoverDayOpen, setHolidayName, setCurrentHolidayId, holidayAnchor) => {
    const showHolidayInfo = (e, day) => {
        // Find the topmost holiday container element (the one with calendar-holiday class)
        let holidayElement = e.target;
        while (holidayElement && !holidayElement.classList.contains('calendar-holiday')) {
            holidayElement = holidayElement.parentElement;
        }
        
        if (holidayElement && holidayElement.classList.contains('calendar-holiday')) {
            // Clear any previous anchor
            if (holidayAnchor.current) {
                holidayAnchor.current = null;
            }
            
            // Set the new anchor to the topmost holiday container
            holidayAnchor.current = holidayElement;
            
            // Set current holiday ID for tracking
            const holidayId = `${day.info.name}-${day.date}`;
            setCurrentHolidayId(holidayId);
            
            // Fix typo in holiday name
            if (day.info.name === "Kristi Himmelsprettsdag") {
                setHolidayName("Kristi Himmelfartsdag")
            } else {
                setHolidayName(day.info.name)
            }
            setIsHoverDayOpen(true);
        } else {
            // Clear anchor and don't show popup if we don't have a valid anchor
            holidayAnchor.current = null;
            setCurrentHolidayId(null);
            setIsHoverDayOpen(false);
        }
    }

    return (day) => {
        // Add safety check for day parameter
        if (!day || !day.date) {
            console.warn('createMyDayContent: Invalid day parameter:', day);
            return <div className="header-date-container">Error</div>;
        }

        const date = day.date;
        
        // Ensure date is a valid Date object
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            console.warn('createMyDayContent: Invalid date object:', date);
            return <div className="header-date-container">Invalid Date</div>;
        }

        let formattedDay;
        try {
            formattedDay = formatDate('DD.MM.YY', date);
        } catch (error) {
            console.error('createMyDayContent: Error formatting day:', error);
            return <div className="header-date-container">Format Error</div>;
        }

        const holidayDates = []
        // Add safety check to prevent forEach error
        if (norwegianHolidays && Array.isArray(norwegianHolidays)) {
            norwegianHolidays.forEach((holi) => {
                if (holi && holi.date) {
                    try {
                        const formattedHolidayDate = formatDate('DD.MM.YY', new Date(holi.date));
                        holidayDates.push({
                            date: formattedHolidayDate,
                            info: holi
                        })
                    } catch (error) {
                        console.warn('createMyDayContent: Error processing holiday:', holi, error);
                    }
                }
            })
        }

        // HOLIDAYS
        if (holidayDates.length > 0 && holidayDates.map((date) => date.date).includes(formattedDay)) {
            var index = holidayDates.findIndex(v => v.date.includes(formattedDay));
            return <div
                className={`header-date-container calendar-holiday ${formatDate('YYYY-MM-DD', date)}`}
                onMouseOver={(e) => { 
                    showHolidayInfo(e, holidayDates[index]); 
                }}
            >
                <div className='header-date-day'>{formatDate('DDD', date)}</div>
                <p className='header-date-date'>{formatDate('DD.MM.YY', date)}</p>
            </div>;
        }
        // SATURDAY
        if (day.date.getDay() === 6) {
            return <div className={`header-date-container calendar-saturday ${formatDate('YYYY-MM-DD', date)}`}>
                <div className='header-date-day'>{formatDate('DDD', date)}</div>
                <p className='header-date-date'>{formatDate('DD.MM.YY', date)}</p>
            </div>;
        }
        // SUNDAY
        else if (day.date.getDay() === 0) {
            return <div className={`header-date-container calendar-sunday ${formatDate('YYYY-MM-DD', date)}`}>
                <div className='header-date-day'>{formatDate('DDD', date)}</div>
                <p className='header-date-date'>{formatDate('DD.MM.YY', date)}</p>
            </div>;
        }
        // TODAY
        else if (formatDate('YYYY-MM-DD', day.date) === formatDate('YYYY-MM-DD', new Date())) {
            return <div style={{ backgroundColor: "rgb(227, 255, 227)" }} className={`header-date-container calendar-sunday ${formatDate('YYYY-MM-DD', date)}`}>
                <div className='header-date-day'>{formatDate('DDD', date)}</div>
                <p className='header-date-date'>{formatDate('DD.MM.YY', date)}</p>
            </div>;
        }

        return <div className={`header-date-container ${formatDate('YYYY-MM-DD', date)}`}>
            <div className='header-date-day'>{formatDate('DDD', date)}</div>
            <p className='header-date-date'>{formatDate('DD.MM.YY', date)}</p>
        </div>;
    };
};

// Function to set Norwegian holidays
export const setNorwegianHolidays = (mySelectedDate) => {
    try {
        // Check if holidays package is available
        if (typeof holidays !== 'function') {
            console.error('setNorwegianHolidays: holidays package not available');
            return [];
        }

        if (!mySelectedDate) {
            mySelectedDate = new Date();
        }
        
        const year = formatDate('YYYY', mySelectedDate);
        const holidaysData = holidays(year);
        
        if (!holidaysData || !Array.isArray(holidaysData)) {
            console.warn(`setNorwegianHolidays: Invalid holidays data for year ${year}`, holidaysData);
            return [];
        }
        
        return holidaysData;
    } catch (error) {
        console.error('setNorwegianHolidays: Error loading holidays:', error);
        return [];
    }
};
